"use client";

import { useHandyoutEvents } from "@/hooks/use-handyout-events";
import { fetchApi, mutateApi } from "@/lib/api-fetch";
import { queryKeys } from "@/lib/query-keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type ReactionValue = 0 | 1 | null;

export interface ReactionCounts {
  up: number;
  down: number;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

async function fetchReaction(slideId: string): Promise<ReactionValue> {
  const data = await fetchApi<{ reaction?: unknown }>(
    `/api/reactions?slide_id=${slideId}`,
  ).catch(() => null);
  const value = data?.reaction;
  return value === 0 || value === 1 ? value : null;
}

async function fetchReactionCounts(slideId: string): Promise<ReactionCounts> {
  const data = await fetchApi<Partial<ReactionCounts>>(
    `/api/reactions/counts?slide_id=${slideId}`,
  ).catch(() => null);
  return {
    up: typeof data?.up === "number" ? data.up : 0,
    down: typeof data?.down === "number" ? data.down : 0,
  };
}

export function useReactionQuery(
  slideId: string,
  mode: "interactive" | "editor",
  handyoutId?: string,
) {
  const queryClient = useQueryClient();
  const enabled = !!slideId && UUID_RE.test(slideId) && mode === "interactive";

  const query = useQuery({
    queryKey: queryKeys.reactions.bySlide(slideId),
    queryFn: () => fetchReaction(slideId),
    enabled,
  });

  useHandyoutEvents(enabled ? handyoutId : undefined, {
    onEvent: (event) => {
      if (event.type !== "reactions") return;
      if (
        event.slide_id &&
        queryClient.isMutating({
          mutationKey: queryKeys.reactions.set(event.slide_id),
        }) > 0
      ) {
        return;
      }
      if (!event.slide_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.reactions.all });
        return;
      }
      // Two sibling keys, so the per-slide invalidation has to name both.
      queryClient.invalidateQueries({
        queryKey: queryKeys.reactions.bySlide(event.slide_id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.reactions.countsBySlide(event.slide_id),
      });
    },
    onResync: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reactions.all });
    },
  }, { audience: "public" });

  return query;
}

export function useReactionCountsQuery(
  slideId: string,
  mode: "interactive" | "editor",
) {
  const enabled = !!slideId && UUID_RE.test(slideId) && mode === "interactive";

  return useQuery({
    queryKey: queryKeys.reactions.countsBySlide(slideId),
    queryFn: () => fetchReactionCounts(slideId),
    enabled,
    placeholderData: (previous) => previous,
  });
}

function applyVoteChange(
  counts: ReactionCounts,
  from: ReactionValue,
  to: ReactionValue,
): ReactionCounts {
  if (from === to) return counts;
  let { up, down } = counts;
  if (from === 1) up -= 1;
  if (from === 0) down -= 1;
  if (to === 1) up += 1;
  if (to === 0) down += 1;
  return { up: Math.max(0, up), down: Math.max(0, down) };
}

/**
 * Clicks on one slide run one after another (mutation scope), and each click
 * moves both the own vote and the totals optimistically from whatever the cache
 * shows at that moment, so overlapping clicks chain instead of racing. Only the
 * last click in flight rolls back or reconciles: an earlier click settling would
 * otherwise overwrite the optimistic state of the ones still queued behind it.
 */
export function useSetReactionMutation(slideId: string) {
  const queryClient = useQueryClient();
  const voteKey = queryKeys.reactions.bySlide(slideId);
  const countsKey = queryKeys.reactions.countsBySlide(slideId);
  const mutationKey = queryKeys.reactions.set(slideId);
  const isLastInFlight = () => queryClient.isMutating({ mutationKey }) <= 1;

  return useMutation({
    mutationKey,
    scope: { id: `reactions:${slideId}` },
    mutationFn: async (reaction: ReactionValue) => {
      if (reaction === null) {
        await mutateApi(
          "DELETE",
          `/api/reactions?slide_id=${slideId}`,
        );
      } else {
        await mutateApi("POST", "/api/reactions", {
          slide_id: slideId,
          reaction,
        });
      }
      return reaction;
    },
    onMutate: async (reaction) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: voteKey }),
        queryClient.cancelQueries({ queryKey: countsKey }),
      ]);
      const previousVote = queryClient.getQueryData<ReactionValue>(voteKey);
      const previousCounts =
        queryClient.getQueryData<ReactionCounts>(countsKey);
      queryClient.setQueryData<ReactionValue>(voteKey, reaction);
      if (previousCounts) {
        queryClient.setQueryData<ReactionCounts>(
          countsKey,
          applyVoteChange(previousCounts, previousVote ?? null, reaction),
        );
      }
      return { previousVote, previousCounts };
    },
    onError: (_err, _vars, context) => {
      if (!context || !isLastInFlight()) return;
      if (context.previousVote !== undefined) {
        queryClient.setQueryData(voteKey, context.previousVote);
      }
      if (context.previousCounts !== undefined) {
        queryClient.setQueryData(countsKey, context.previousCounts);
      }
    },
    onSettled: () => {
      if (!isLastInFlight()) return;
      queryClient.invalidateQueries({ queryKey: voteKey });
      queryClient.invalidateQueries({ queryKey: countsKey });
    },
  });
}
